<template>
   <div v-if="blogpost" class="blogpost-container min-h-screen w-full">
      <div id="render-element" class="header relative mx-auto mt-16 print:mt-0 max-w-2xl">
         <!-- Header section -->
         <div v-if="hasHeaderSection" data-test="blog-content-header-sect">
            <div class="mx-auto w-full shrink-0 print:hidden">
               <div class="header-image relative h-full min-h-80">
                  <LazyLoadingBGimage id="image" :mainSrc="blogpost.imageHero_620w" />
               </div>
            </div>
            <div class="header-text mt-4 flex min-h-5 w-full flex-col justify-center">
               <h1 v-if="!isTitleHidden" class="h2-container text-header mt-6 print:mt-0" data-test="blogpost-title">
                  {{ blogpost.title }}
               </h1>
               <p v-if="blogpost.subtitle" class="mt-6 pb-4 text-lg leading-normal" data-test="blogpost-subtitle">
                  {{ blogpost.subtitle }}
               </p>
               <RichTextRenderer
                  class="richtext-header hidden pt-2"
                  data-test="header-content-desktop"
                  :document="blogpost.content.header"
               />
            </div>
         </div>
         <div v-if="hasDivider" class="multi-section-div print:hidden mx-6 mt-6 block h-px bg-grey-20"></div>
      </div>
      <!-- Rich-text content -->
      <div class="richtext-container relative mx-auto print:my-4 my-10 max-w-lg">
         <RichTextRenderer
            class="richtext-content"
            data-test="header-content-mobile"
            :document="blogpost.content.header"
         />
         <RichTextRenderer :document="blogpost.content.body" data-test="body-content" />
      </div>
   </div>
</template>

<script setup lang="ts">
import type { Blogpost } from "@/data/api-layer/blogpostsApi";

interface Props {
   blogpost: Blogpost;
   hasHeaderSection?: boolean;
   isTitleHidden?: boolean; // Hide the blogpost title in the Education flow block
}

const props = withDefaults(defineProps<Props>(), {
   hasHeaderSection: false,
   isTitleHidden: false,
});

const hasDivider = computed(() => {
   return props.hasHeaderSection && props.blogpost?.content?.body?.content?.length > 1;
});
</script>

<style scoped>
.blogpost-container {
   container-type: inline-size;
}

@media print {
   @page {
      margin-top: 1.5cm;
   }
   @page :first {
      margin-top: 0;
   }
}
@media screen {
   /* screen CUSTOM */
   @container (max-width: 575px) {
      .header-image {
         @apply min-h-[16rem];
      }
   }

   /* screen MD */
   @container (min-width: 48em) {
      .header {
         @apply mt-[4.75rem];
      }

      .header > div {
         display: flex;
      }

      .header > div > div {
         width: 50%;
      }

      .header-image {
         @apply mr-0;
      }

      .header-text {
         @apply mt-0 w-1/2 ml-5;
      }

      .multi-section-div {
         @apply mt-12;
      }
   }

   /* screen LG */
   @container (min-width: 62em) {
      .header {
         @apply mt-[6.75rem];
      }

      .header-image {
         @apply ml-10 min-h-[25rem];
      }

      .header-text {
         @apply px-10;
      }

      .multi-section-div {
         @apply mx-10 mt-20;
      }

      .richtext-container {
         @apply px-0 my-16;
      }

      .richtext-content {
         @apply hidden;
      }

      .richtext-header {
         @apply block;
      }
   }

   /* screen XL */
   @container (min-width: 80em) {
      .header-image {
         @apply ml-0;
      }

      .header-text {
         @apply pr-0;
      }

      .multi-section-div {
         @apply mx-0;
      }
   }
}
</style>
